import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import UserMenu from './UserMenu';
import { Menu, X, Sparkles, TrendingUp } from 'lucide-react';

function cls(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}

interface NavItem {
  label: string;
  path: string;
  requiresAuth: boolean;
}

const Header: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems: NavItem[] = [
    { label: 'Economy', path: '/research', requiresAuth: false },
    { label: 'Reports', path: '/reports', requiresAuth: false },
    { label: 'News', path: '/news', requiresAuth: false },
    { label: 'Trading', path: '/trading', requiresAuth: false },
    { label: 'History', path: '/history', requiresAuth: false },
    { label: 'Stocks', path: '/stocks', requiresAuth: false },
  ];

  // Filter nav items based on auth status
  const visibleNavItems = navItems.filter(item => isAuthenticated || !item.requiresAuth);

  // Don't show header on login/register pages
  if (['/login', '/register', '/verify-email', '/registration-success'].includes(location.pathname)) {
    return null;
  }

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-md backdrop-blur-sm bg-white/95">
      <div className="px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo + Nav */}
          <div className="flex items-center gap-8">
            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-700"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>

            {/* Logo */}
            <Link
              to="/news"
              className="flex items-center gap-2.5 group"
            >
              {/* Logo Icon */}
              <div className="relative">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                  <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3">
                  <Sparkles className="w-3 h-3 text-amber-400 animate-pulse" />
                </div>
              </div>

              {/* Logo Text */}
              <div className="flex flex-col">
                <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-none">
                  FinExus
                </span>
                <span className="text-[10px] text-slate-500 font-medium tracking-wider uppercase leading-none mt-0.5">
                  Financial Intelligence
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {visibleNavItems.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cls(
                      "px-3 py-2 rounded-lg text-sm font-medium transition-all",
                      isActive
                        ? "bg-slate-100 text-slate-900"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Right: User Menu or Login Button */}
          <div>
            {isAuthenticated ? (
              <UserMenu />
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="hidden sm:block px-5 py-2 text-sm bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all shadow-md"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && visibleNavItems.length > 0 && (
        <div className="lg:hidden border-t border-slate-200 bg-white shadow-lg">
          <nav className="px-4 py-3 space-y-1">
            {visibleNavItems.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cls(
                    "block px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
            {!isAuthenticated && (
              <button
                onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                className="w-full mt-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-sm font-semibold text-center"
              >
                Sign In
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
